import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { orderClient } from '../../data/order';
import { t } from 'i18next';
import { userInfo } from 'os';
import { userClient } from '../../data/user';
import { customerVerificationClient } from '../../data/verify-info';
import { toast } from 'react-toastify';

const PaymentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const acquiredRef = useRef<any>(null);
  const publicKey = process.env.REACT_APP_ACQUIRED_PUBLIC_KEY ?? "";
  const [accComponents, setComponents] = useState(null);

  const [acquiredLoaded, setAcquiredLoaded] = useState(false);
  const [customerinfo, setCustomerinfo] = useState<any>(null);
  const [termsAndConditionLink, setTermsAndConditionLink] = useState<string>('');
  const [totalPayablAmount, setTotalPayableAmount] = useState<number>(0);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(false);
  const token = searchParams.get('session');
  const navigate = useNavigate();

  const loadAcquiredScript = () => {
    const script = document.createElement("script");
    const scriptUrl = process.env.REACT_APP_ACQUIRED_SRC_URL;
    script.src = scriptUrl!;
    script.async = true;
    script.integrity = process.env.REACT_APP_ACQUIRED_INTEGRAITY_KEY!;
    script.crossOrigin = process.env.REACT_APP_ACQUIRED_CROSSORIGIN_METHOD!;
    script.onload = () => setAcquiredLoaded(true);
    document.body.appendChild(script);
  };

  (function (global) {
    if (!global.Acquired) {
      global.Acquired = {}; // Initialize Acquired only if it doesn't exist
      // Your script logic here
      if (window.Acquired && !acquiredLoaded) {
        // `Acquired` is already defined and is a constructor function
        setAcquiredLoaded(true);
        loadAcquiredScript();
        return;
      }
    }
  })(window);

  const initializeAcquired = () => {
    acquiredRef.current = new Acquired(publicKey);
    const paymentEnv = process.env.REACT_APP_ACQUIRED_PAYMENT_ENV ?? "";
    const options = {
      session: sessionId, // Fixed case sensitivity
      environment: paymentEnv,
    };
    const components = acquiredRef.current.components(options);

    const style = {
      base: {
        color: "black",
        backgroundColor: "#fff",
        padding: "1rem 1rem",
        fontFamily: "Poppins",
        border: "1px solid #cccccc",
        borderRadius: "13px",
        background: "#ffffff",
        height: "58px",
        margin: "0",
      },
      focus: {
        color: "green",
        borderColor: "green",
      },
      invalid: {
        color: "#dc3545",
        borderColor: "#dc3545",
      },
      placeholder: {
        color: "gray",
      },
    };

    components.create("payment", { style }).mount("#payment-component");
    components.on("blur", (response: any) => {
      acquiredRef.current.showComponentErrorMessage(
        ".component-error",
        response
      );
      // document.querySelector('#error-message')!.textContent = JSON.stringify(response);
    });
    setComponents(components);

  };

  const handleSubmit = async () => {
    const confirmParams = {
      payment: {
        create_card: true,
      },
      customer: {
        first_name: customerinfo?.firstName,
        last_name: customerinfo?.lastName,
        dob: customerinfo?.dob,
        ip: "127.0.0.1",
        billing: {
          address: {
            line_1: customerinfo?.billingAddress?.housename,
            line_2: customerinfo?.billingAddress?.street,
            city: customerinfo?.billingAddress?.town,
            state: "",
            postcode: customerinfo?.billingAddress?.postalcode,
            country_code: "",
          },
          email: customerinfo?.email,
          phone: {
            country_code: "",
            number: customerinfo?.phone,
          },
        },
      },
      webhook_url: "",
    };
    try {
      const response = await acquiredRef.current
        .confirmPayment({
          components: accComponents,
          confirmParams: confirmParams,
        })
        .catch((error: any) => {
        });

      if (response.isTdsPending()) {
        window.location.href = response.data.redirect_url;
      } else if (response.isSuccess()) {
        const orderId = response.data.order_id || "";
        navigate(`/congratulations?orderId=${orderId}&status=success`);
      } else if (response.isError()) {

        acquiredRef.current.showComponentErrorMessage(
          ".component-error",
          response.data
        );
      }
      if (response?.data?.status === "error") {
        toast.error(response?.data?.message);
      }
    } catch (error: any) {
      console.log("Error::", error);
      console.error("Payment submission error:", error);
    }
    setSubmitted(true); // Handle the submit logic here
  };

  // Initialize session and load payment data
  useEffect(() => {
    const verifySession = async () => {
      if (!token) {
        setError('No session token provided');
        setIsLoading(false);
        return;
      }

      try {
        // 1. First initiate the order purchase to get the session ID
        const { data: orderResponse } = await orderClient.initiateOrderPurchase(token);

        if (orderResponse?.session_id) {
          // 2. Set the session ID from the order response
          setSessionId(orderResponse.session_id);

          // 3. Verify the payment session with the token
          const { data: verificationResponse } = await orderClient.verifyPaymentSession({ token });
          setCustomerinfo(verificationResponse.customer);
          setTermsAndConditionLink(verificationResponse.purchaseTermsLink);
          // 4. Set the total payable amount from verification response
          if (verificationResponse?.amount) {
            setTotalPayableAmount(verificationResponse.amount);
            setAcquiredLoaded(true);
          } else {
            setError('Invalid session data');
          }
        } else {
          setError('Failed to initialize payment session');
        }
      } catch (err) {
        setError('Failed to process payment');
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      verifySession();
    } else {
      setIsLoading(false);
      setError('No session token provided');
    }
  }, [token]);

  // Initialize Acquired once the script is loaded and we have a session ID
  useEffect(() => {
    if (acquiredLoaded && sessionId) {
      const timer = setTimeout(() => {
        if (window.Acquired) {
          initializeAcquired();
          setIsLoading(false);
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [acquiredLoaded, sessionId]);

  // Render loading state
  if (isLoading) {
    return (
      <div className="payment-page">
        <h1>Processing Payment...</h1>
        <p>Please wait while we verify your payment details.</p>
      </div>
    );
  }

  // Handle error state
  if (error) {
    // Redirect to 404 page on any error
    // window.location.href = '/not-found';
    return null; // This will be replaced by the 404 page
  }

  return (
    <div className="payment-page">
      {/* Add your payment form components here */}
      <h4 className="ownit-popup-title mt-3 text-center"> Try it, love it. <span className="ownit-text-blackgreen">OWN<span>it.</span></span></h4><p className="text-center">Try-before-you-buy.</p>

      {isLoading && <div className="payment-form-loader"><h4>Loading<span className="payment-loader-dotes"></span></h4></div>}
      <form id="payment-form" className="form-main px-4" >
        <div id="payment-component"></div>
      </form>
<p className='payment-text'>By making this payment you will buy the item from Retailer.​</p>

      <div className="payment-details-section">
        <div className="form-check mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="termsCheckbox"
            checked={isTermsAccepted}
            onChange={(e) => setIsTermsAccepted(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="termsCheckbox">
            I have read and agree to the <a 
              href={termsAndConditionLink.startsWith('http') ? termsAndConditionLink : `https://${termsAndConditionLink}`} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Terms and conditions
            </a>
          </label>
        </div>
        <button
          onClick={handleSubmit}
          id="submit"
          className={`btn primary-btn payment-btn ${!isTermsAccepted ? 'disabled' : ''}`}
          disabled={!isTermsAccepted}
        >
          {t("pay-now")}{" "}
          <span>£{Number(totalPayablAmount ?? 0).toFixed(2)}</span>
        </button>
      </div>
    </div>
  );
};

export default PaymentPage;
